import React, { useMemo, useState } from "react";
import { X } from "lucide-react";
import { setAdminLocations } from "services/userService";
import * as Sentry from "@sentry/react";

export const locationLabel = (loc) =>
  `${loc.name || loc.id}${loc.table ? ` — ${loc.table}` : ""}`;

/**
 * Users table cell listing the locations a user administers, each removable,
 * with a dropdown to add any other location.
 */
const AdminControlCell = ({ user, locations, refreshUsers }) => {
  const [saving, setSaving] = useState(false);
  const adminLocations = useMemo(() => user.adminLocations || [], [user.adminLocations]);
  const locationsById = useMemo(
    () => Object.fromEntries(locations.map((l) => [l.id, l])),
    [locations]
  );
  const addable = useMemo(
    () =>
      locations
        .filter((l) => !l.discarded && !adminLocations.includes(l.id))
        .sort(
          (a, b) =>
            Number(!!b.active) - Number(!!a.active) ||
            locationLabel(a).localeCompare(locationLabel(b), "fr")
        ),
    [locations, adminLocations]
  );

  const save = async (ids) => {
    setSaving(true);
    try {
      await setAdminLocations(user.id, ids);
      await refreshUsers();
    } catch (error) {
      Sentry.captureException(error);
      console.error("Erreur mise à jour admin:", error);
      window.alert("Erreur lors de la mise à jour des droits d'admin.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <td className="p-2">
      <div className="flex flex-col gap-1.5 min-w-[14rem]">
        {adminLocations.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {adminLocations.map((id) => {
              const loc = locationsById[id];
              const gone = !loc || loc.discarded;
              const text = loc ? locationLabel(loc) : id;
              return (
                <span
                  key={id}
                  className={`inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full text-xs ${
                    gone ? "bg-gray-700 text-gray-400 italic" : "bg-green-900/60 text-green-200"
                  }`}
                  title={id}
                >
                  {gone ? `${text} (supprimé)` : text}
                  <button
                    type="button"
                    onClick={() => save(adminLocations.filter((x) => x !== id))}
                    disabled={saving}
                    className="p-0.5 rounded-full hover:bg-red-600 hover:text-white disabled:opacity-50"
                    title="Retirer ce lieu"
                    aria-label={`Retirer ${text}`}
                  >
                    <X size={12} />
                  </button>
                </span>
              );
            })}
          </div>
        )}
        {addable.length > 0 && (
          <select
            value=""
            disabled={saving}
            onChange={(e) => e.target.value && save([...adminLocations, e.target.value])}
            className="bg-gray-700 text-white px-2 py-1 rounded border border-gray-600 text-xs disabled:opacity-50"
          >
            <option value="">
              {adminLocations.length > 0 ? "+ Ajouter un lieu" : "Rendre admin d'un lieu…"}
            </option>
            {addable.map((loc) => (
              <option key={loc.id} value={loc.id}>
                {locationLabel(loc)}
                {loc.active ? "" : " (inactif)"}
              </option>
            ))}
          </select>
        )}
      </div>
    </td>
  );
};

export default AdminControlCell;
